import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import {
  useAttributeGroups,
  useAttributes,
  useCatalogLanguages,
  useOkeiUnits,
  useProduct,
  useProductBarcodes,
  useProductPublication,
  useProductPriceLines,
  useProductStock,
  useProductTranslations,
  useProductVariants,
  useProductVersion,
  useProductVersions,
  useResetProductCategoryOverride,
  useResetProductImagesOverride,
  useRestoreProductVersion,
  useSearchCategories,
  useUpdateProductState,
  useUpsertProduct,
} from '../../hooks';
import { useCardLanguageDraft } from '../../lib/useCardLanguageDraft';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatDateTime, formatNumber, formatPrice, formatQuantity } from '../../lib/format';
import { resolveLocalizedText } from '../../lib/localizedText';
import {
  governingBaseLine,
  groupBySlot,
  isBaseSlot,
  pendingBaseSubmission,
  scheduledBaseLine,
  unitAmountOf,
} from '../../lib/priceSlots';
import { computeReadiness } from '../../lib/readiness';
import { useResolvedProductFamily } from '../../lib/useResolvedProductFamily';
import { useToast } from '../../lib/useToast';
import type { ToastSeverity } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  COMMERCIAL_AVAILABILITY_OPTIONS,
} from '../../model/options';
import type {
  CommercialAvailability,
  OkeiUnit,
  PriceLineResponse,
  ProductAttribute,
  ProductTranslationInput,
  ProductUpsertRequest,
} from '../../model/types';
import { CardLanguageSwitcher } from '../components/CardLanguageSwitcher';
import { MediaManager } from '../components/MediaManager';
import { ProductAttributesPanel } from '../components/ProductAttributesPanel';
import { ProductBarcodesPanel } from '../components/ProductBarcodesPanel';
import { ProductFamilyPanel } from '../components/ProductFamilyPanel';
import { ProductPublicationCard } from '../components/ProductPublicationCard';
import { ProductPricesDrawer } from '../components/ProductPricesDrawer';
import { ProductSectionDrawer } from '../components/ProductSectionDrawer';
import { ProductStockPanel } from '../components/ProductStockPanel';
import { ProductSummaryTiles } from '../components/ProductSummaryTiles';
import type { ProductSummaryTile, ProductSummaryTileBadge } from '../components/ProductSummaryTiles';
import { ProductVariantsSection } from '../components/ProductVariantsSection';
import { ReadinessChecklist } from '../components/ReadinessChecklist';
import { VersionHistoryDrawer } from '../components/VersionHistoryDrawer';

// ── Local types ──────────────────────────────────────────────────────────────

interface ProductDraft {
  code: string;
  categoryId: string;
  categoryName: string;
  brand: string;
  /** Display-only unit label — kept in sync with `unitOkeiCode`'s `symbolRu` once one is chosen. */
  unit: string;
  /** OKEI unit-of-measure code (plan-catalog-008) — set once a dictionary entry is picked. */
  unitOkeiCode?: string;
  attributes: ProductAttribute[];
  images: string[];
  commercialAvailability: CommercialAvailability;
}

const EMPTY_DRAFT: ProductDraft = {
  code: '',
  categoryId: '',
  categoryName: '',
  brand: '',
  unit: '',
  unitOkeiCode: undefined,
  attributes: [],
  images: [],
  commercialAvailability: 'AVAILABLE',
};

/** Which left-anchored detail drawer is open (adr-merchant-portal-008) — at most one at a time. */
type ProductDrawerKey = 'prices' | 'stock' | 'barcodes' | 'variants';

// ── Card-language switcher (epic-catalog-004, adr-tenant-005, plan-tenant-004 Wave 2) ─────────
// `name`/`description` moved out of `ProductDraft` into a per-locale draft (`useCardLanguageDraft`)
// — see that hook's doc for the empty-registry single-language fallback. A module-level constant
// (not recreated per render) keeps `useCardLanguageDraft`'s internal memoization stable.
interface ProductLocaleFields {
  name: string;
  description: string;
}
const PRODUCT_LOCALE_EMPTY_FIELDS: ProductLocaleFields = { name: '', description: '' };

// ── Customer state preview ────────────────────────────────────────────────────

/**
 * i18next keys for the backend-computed CustomerState.state values — render with
 * `t(CUSTOMER_STATE_LABELS[state])`. Six of the eight states are the exact same concept as
 * `model/options.ts`'s `COMMERCIAL_AVAILABILITY_LABELS` (CustomerState is derived from
 * commercialAvailability on the backend), so those keys are reused directly; `NO_PRICE` and
 * `UNAVAILABLE` are computed-only states with no CommercialAvailability equivalent and get
 * their own `productEdit.customerState.*` keys.
 */
const CUSTOMER_STATE_LABELS: Record<string, string> = {
  ...COMMERCIAL_AVAILABILITY_LABELS,
  NO_PRICE: 'catalog:productEdit.customerState.noPrice',
  UNAVAILABLE: 'catalog:productEdit.customerState.unavailable',
};

/**
 * Compact read-only panel showing the backend-computed buyer state.
 * Placed inside the status card so merchants see it alongside the readiness checklist.
 */
function CustomerStatePreview({
  state,
  orderable,
  showPrice,
  reasonCode,
}: {
  state: string;
  orderable: boolean;
  showPrice: boolean;
  reasonCode?: string | null;
}) {
  const { t } = useTranslation('catalog');
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: orderable ? 'success.50' : 'action.hover',
        border: '1px solid',
        borderColor: orderable ? 'success.200' : 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {t('productEdit.customerState.currentStateLabel')}
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={CUSTOMER_STATE_LABELS[state] ? t(CUSTOMER_STATE_LABELS[state]) : state}
          color={orderable ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          size="small"
          label={
            orderable
              ? t('productEdit.customerState.orderable')
              : t('productEdit.customerState.notOrderable')
          }
          color={orderable ? 'success' : 'error'}
          variant={orderable ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          label={
            showPrice
              ? t('productEdit.customerState.priceVisible')
              : t('productEdit.customerState.priceHidden')
          }
          color={showPrice ? 'success' : 'default'}
          variant="outlined"
        />
        {reasonCode ? (
          <Typography variant="caption" color="text.secondary">
            {t('productEdit.customerState.reason', { code: reasonCode })}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

// ── Unit-of-measure field (plan-catalog-008: OKEI dictionary) ────────────────

interface ProductUnitFieldProps {
  /**
   * Display-only unit label (`ProductDraft.unit`) — mirrors the selected OKEI unit's `symbolRu`,
   * or an as-yet-unclassified legacy value (free text with no matching `unitOkeiCode`).
   */
  unit: string;
  unitOkeiCode: string | undefined;
  onChange: (next: { unit: string; unitOkeiCode: string | undefined }) => void;
  disabled: boolean;
}

/**
 * Dictionary-driven unit-of-measure selector (plan-catalog-008 Phase 5) — replaces the legacy
 * free-text `unit` field with an Autocomplete over the OKEI reference dictionary
 * (`useOkeiUnits`/`GET /api/v1/catalog/units/okei`, cached ~1h client-side as near-static
 * reference data). Renders all four data states explicitly:
 *
 *  - **Loading** — a disabled input showing a "loading units…" placeholder.
 *  - **Error** — falls back to a PLAIN free-text field bound directly to `unit`, so a dictionary
 *    outage never blocks saving the rest of the product.
 *  - **Empty** (dictionary loaded with zero entries — not expected once seeded) — covered by the
 *    Autocomplete's own translated "no matching units" message.
 *  - **Populated** — a single-select Autocomplete; picking an option stores its `code` as
 *    `unitOkeiCode` and mirrors its `symbolRu` into the legacy `unit` display field (both travel
 *    together on save — see `ProductEditPage.handleSave`).
 *
 * `freeSolo` lets an existing legacy product (free-text `unit`, no `unitOkeiCode` yet) keep
 * showing its current value verbatim instead of appearing blank or triggering MUI's "value does
 * not match any option" warning, alongside a hint nudging the merchant to reclassify it from the
 * list; typing further free text (no dictionary match) preserves today's free-text behaviour
 * (`unitOkeiCode` stays unset) rather than blocking the merchant from saving.
 */
function ProductUnitField({ unit, unitOkeiCode, onChange, disabled }: ProductUnitFieldProps) {
  const { t } = useTranslation('catalog');
  const okeiUnitsQuery = useOkeiUnits();
  const okeiUnits = okeiUnitsQuery.data ?? [];
  const selectedUnit = okeiUnits.find((option) => option.code === unitOkeiCode) ?? null;
  const isLegacyUnclassified = !selectedUnit && unit.trim() !== '';

  // Error state: never let a dictionary outage block editing the rest of the product — fall back
  // to the original plain free-text control.
  if (okeiUnitsQuery.isError) {
    return (
      <TextField
        label={t('productEdit.unitLabel')}
        placeholder={t('productEdit.unitPlaceholder')}
        value={unit}
        onChange={(e) => onChange({ unit: e.target.value, unitOkeiCode: undefined })}
        helperText={t('productEdit.unitOkei.errorHint')}
        disabled={disabled}
        fullWidth
      />
    );
  }

  return (
    <Autocomplete<OkeiUnit, false, false, true>
      freeSolo
      fullWidth
      options={okeiUnits}
      loading={okeiUnitsQuery.isLoading}
      disabled={disabled || okeiUnitsQuery.isLoading}
      value={selectedUnit ?? (unit || null)}
      onChange={(_event, newValue) => {
        if (newValue == null) {
          onChange({ unit: '', unitOkeiCode: undefined });
        } else if (typeof newValue === 'string') {
          onChange({ unit: newValue.trim(), unitOkeiCode: undefined });
        } else {
          onChange({ unit: newValue.symbolRu, unitOkeiCode: newValue.code });
        }
      }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : `${option.nameRu} (${option.symbolRu})`
      }
      isOptionEqualToValue={(option, value) => typeof value !== 'string' && option.code === value.code}
      noOptionsText={t('productEdit.unitOkei.noOptions')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('productEdit.unitLabel')}
          placeholder={
            okeiUnitsQuery.isLoading
              ? t('productEdit.unitOkei.loadingPlaceholder')
              : t('productEdit.unitOkei.placeholder')
          }
          helperText={isLegacyUnclassified ? t('productEdit.unitOkei.legacyHint') : undefined}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {okeiUnitsQuery.isLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

// ── Price tile: badge + captions (design spec §8.1, plan-merchant-portal-018 WP1.3) ─────────────
// Both helpers are pure presentation functions over `priceSlots.ts`'s existing derivations
// (`pendingBaseSubmission`, `scheduledBaseLine`) — they never re-derive which line governs,
// schedules or is newest-pending (adr-merchant-portal-018 Decision 4). Kept local to this file per
// this feature's own convention of not sharing tiny presentation helpers across components (see
// `ProductPricesDrawer.tsx`'s `stateColor`/`suspicionLabel` doc comments for the same rationale) —
// `ProductPricesDrawer`'s copy is the fuller, translated-reason version this file's badge tooltip
// mirrors for BLOCKED/REJECTED.

const TILE_SUSPICION_LABEL_KEYS: Record<string, string> = {
  DROP_TO_ZERO: 'productEdit.pricesDrawer.suspicion.dropToZero',
  SUSPICIOUS_DROP: 'productEdit.pricesDrawer.suspicion.suspiciousDrop',
  EXCESSIVE_DISCOUNT: 'productEdit.pricesDrawer.suspicion.excessiveDiscount',
  LARGE_DEVIATION: 'productEdit.pricesDrawer.suspicion.largeDeviation',
};

function tileSuspicionReasonLabel(
  t: (key: string) => string,
  suspicionReason?: string,
  reason?: string,
): string {
  if (suspicionReason && TILE_SUSPICION_LABEL_KEYS[suspicionReason]) {
    return t(TILE_SUSPICION_LABEL_KEYS[suspicionReason]);
  }
  return reason ?? suspicionReason ?? '';
}

/**
 * The hero tile's governance badge (design spec §8.1) — `line` is
 * `pendingBaseSubmission(lines, governing)`'s result: the newest non-ACTIVE base submission newer
 * than the governing line, or any when there is none. A scheduled line never reaches here (it is
 * ACTIVE, so `pendingBaseSubmission` never returns one) and neither does EXPIRED — both "never
 * badge" per §8.0/§8.1.
 */
function priceTileBadge(
  line: PriceLineResponse | undefined,
  t: (key: string, vars?: Record<string, unknown>) => string,
): ProductSummaryTileBadge | undefined {
  if (!line) return undefined;
  const price = formatPrice(unitAmountOf(line));
  switch (line.state) {
    case 'REQUIRES_APPROVAL':
      return {
        label: t('productEdit.summary.price.badge.requiresApproval'),
        color: 'warning',
        tooltip: t('productEdit.summary.price.badgeTooltip.requiresApproval', { price }),
      };
    case 'BLOCKED':
      return {
        label: t('productEdit.summary.price.badge.blocked'),
        color: 'error',
        tooltip: t('productEdit.summary.price.badgeTooltip.blocked', {
          reason: tileSuspicionReasonLabel(t, line.suspicionReason, line.reason),
        }),
      };
    case 'REJECTED':
      return {
        label: t('productEdit.summary.price.badge.rejected'),
        color: 'default',
        tooltip: t('productEdit.summary.price.badgeTooltip.rejected', {
          reason: tileSuspicionReasonLabel(t, line.suspicionReason, line.reason),
        }),
      };
    default: // DRAFT | PENDING_VALIDATION
      return {
        label: t('productEdit.summary.price.badge.pending'),
        color: 'default',
        tooltip: t('productEdit.summary.price.badgeTooltip.pending', { price }),
      };
  }
}

const PENDING_BADGE_STATES = new Set(['REQUIRES_APPROVAL', 'PENDING_VALIDATION', 'DRAFT']);

/**
 * The hero tile's caption stack (design spec §8.1) — at most two lines, precedence: scheduled
 * ("From {date} — {price}") › other-slots ("N more prices…") › compare-at ("was …"). When there
 * is NO governing line and the newest submission is a PENDING one (not yet a scheduled line, not a
 * BLOCKED/REJECTED one), its own amount takes the scheduled caption's slot instead — the badge
 * chip alone carries no price digits, and this is the only place on the tile that would show them.
 * A BLOCKED/REJECTED submission never gets a caption of its own — its reason lives in the badge's
 * tooltip and in the drawer, not here.
 */
function priceTileCaptions(
  t: (key: string, vars?: Record<string, unknown>) => string,
  params: {
    governing: PriceLineResponse | undefined;
    scheduled: PriceLineResponse | undefined;
    pendingOrNegative: PriceLineResponse | undefined;
    otherSlots: number;
  },
): string[] {
  const { governing, scheduled, pendingOrNegative, otherSlots } = params;
  const candidates: string[] = [];
  if (scheduled) {
    candidates.push(
      t('productEdit.summary.price.scheduled', {
        date: formatDateTime(scheduled.validFrom),
        price: formatPrice(unitAmountOf(scheduled)),
      }),
    );
  } else if (!governing && pendingOrNegative && PENDING_BADGE_STATES.has(pendingOrNegative.state)) {
    candidates.push(
      t(
        pendingOrNegative.state === 'REQUIRES_APPROVAL'
          ? 'productEdit.summary.price.pendingApproval'
          : 'productEdit.summary.price.pendingCheck',
        { price: formatPrice(unitAmountOf(pendingOrNegative)) },
      ),
    );
  }
  if (otherSlots > 0) {
    candidates.push(t('productEdit.summary.price.otherSlots', { count: otherSlots }));
  }
  if (governing?.compareAtAmount != null) {
    candidates.push(t('productEdit.summary.price.wasPrice', { price: formatPrice(governing.compareAtAmount) }));
  }
  return candidates.slice(0, 2);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function newProductId(): string {
  // crypto.randomUUID() is available in all modern browsers (Chrome 92+, Firefox 95+, Safari 15.4+).
  // No fallback — a non-UUID string would fail backend UUID validation on the product endpoint.
  return crypto.randomUUID();
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Create or edit a product (PUT upsert) — the COMPACT product card (adr-merchant-portal-008,
 * supersedes adr-007's three-column stacked layout).
 *
 * ## Layout hierarchy
 *
 * 1. **Hero card (top-left, above the fold)** — photo + gallery, the single card-language
 *    switcher (ONE language shown at a time, applies to every translated field on the page), the
 *    product identity, and the **summary tile strip**: one "current value" tile per hidden detail
 *    block — current price, total stock, primary barcode, variant count. Each tile opens its
 *    block's **left-anchored slide-out drawer** (`ProductSectionDrawer`), so the card stays
 *    compact while losing no data at a glance.
 * 2. **Basic information card** — name, description, SKU, brand, category, unit (name/description
 *    follow the selected card language).
 * 3. **Characteristics card** — the assigned product family (attribute template, auto-resolved on
 *    open via `useResolvedProductFamily`'s completeness fingerprint) + the typed attribute rows
 *    (localizable rows follow the selected card language too).
 * 4. **Right rail** — commercial availability + buyer state + readiness checklist, then the
 *    publication card.
 * 5. **Detail drawers** — Prices (price-engine price lines with validity window),
 *    Stock (availability flag + live per-location snapshot), Barcodes (GTIN management,
 *    immediate mutations), Variants (variant list + generation, immediate mutations).
 */
export function ProductEditPage() {
  const { t, i18n } = useTranslation('catalog');
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const productId = params.productId && params.productId !== 'new' ? params.productId : undefined;
  const isEdit = Boolean(productId);

  const { canView, canManage, canPricingView, canPricingManage } = useCatalogPermissions();
  const resetImagesOverride = useResetProductImagesOverride();
  const resetCategoryOverride = useResetProductCategoryOverride();
  const { toast, success, error: notifyError, clear } = useToast();

  const productQuery = useProduct(productId, { enabled: canView && isEdit });
  const publicationQuery = useProductPublication(productId, { enabled: canView && isEdit });
  const categoriesQuery = useSearchCategories({ page: 0, size: 100, sort: 'name,asc' }, { enabled: canView });
  const upsert = useUpsertProduct();
  const updateState = useUpdateProductState();

  // ── PIM v2: family assignment, typed attributes, variants, version history
  //    (epic-catalog-004/005, user-story-merchant-portal-030/031/032) ────────────────────────
  const dictionaryQuery = useAttributes({ enabled: canView });
  // plan-catalog-009 — drives the attribute panel's group sections (order + translated headers).
  const attributeGroupsQuery = useAttributeGroups({ enabled: canView });
  const languagesQuery = useCatalogLanguages({ enabled: canView });
  const restoreVersion = useRestoreProductVersion();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState<ProductDrawerKey | null>(null);
  const notify = (severity: ToastSeverity, message: string) =>
    severity === 'success' ? success(message) : notifyError(message);

  // `?drawer=prices` deep link (design spec §5/§12) — opens the card with the Prices-360 drawer
  // already showing. Ignored pre-save (no persisted productId to fetch price lines for) and
  // without `catalog.pricing.view` (the tile itself would be disabled anyway — R5). Applied at
  // most once per mount via the ref guard, so closing the drawer afterwards sticks even though the
  // query param itself is left in the URL (a manual re-visit of the same link reopens it again,
  // which is the deep link's whole point).
  const drawerSeedApplied = useRef(false);
  useEffect(() => {
    if (drawerSeedApplied.current) return;
    if (!isEdit || !canPricingView) return;
    if (searchParams.get('drawer') !== 'prices') return;
    drawerSeedApplied.current = true;
    setOpenDrawer('prices');
  }, [isEdit, canPricingView, searchParams]);

  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT);
  const setField = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Seed the draft from the loaded product (edit) once.
  const loaded = productQuery.data;
  useEffect(() => {
    if (!loaded) return;
    setDraft({
      code: loaded.code ?? '',
      categoryId: loaded.categoryId ?? '',
      categoryName: loaded.categoryName ?? '',
      brand: loaded.brand ?? '',
      unit: loaded.unit ?? '',
      unitOkeiCode: loaded.unitOkeiCode ?? undefined,
      attributes: loaded.attributes ?? [],
      // Server contract (web-catalog-master-service, EntityToDocumentMapper.galleryUrls): `images`
      // carries GALLERY rows ONLY — the PRIMARY/cover photo comes separately in `imageUrl`, and the
      // master's media-attach flow even REMOVES the gallery duplicate of the cover ("no demote, no
      // churn"). This draft treats images[0] as the primary (see the save mapping: `imageUrl:
      // draft.images[0]`), so the seed must reconstruct [primary, ...gallery] with the primary
      // deduped. Never bare `??` on `images` here: a single-photo product legitimately comes back
      // as `{imageUrl: <url>, images: []}`, and an empty array is NOT nullish — the previous
      // `loaded.images ?? [imageUrl]` seeded an empty gallery, so the photo vanished on every
      // revisit, and a multi-photo product silently dropped its primary on the next save.
      images: loaded.imageUrl
        ? [loaded.imageUrl, ...(loaded.images ?? []).filter((u) => u !== loaded.imageUrl)]
        : (loaded.images ?? []),
      commercialAvailability: loaded.commercialAvailability ?? 'AVAILABLE',
    });
  }, [loaded]);

  // Assigned product-family resolution (shared by the family panel, the typed-attributes panel and
  // the variants section — see `useResolvedProductFamily`'s doc for why this needs resolving at
  // all). The completeness/value hints let the ASSIGNED TEMPLATE resolve when an existing card is
  // opened, not only after an assignment made in this session (adr-merchant-portal-008).
  const resolvedFamily = useResolvedProductFamily(productId ?? '', loaded?.familyId, {
    attributeCompleteness: loaded?.attributeCompleteness,
    attributeValueCodes: loaded?.attributeValues?.map((v) => v.code),
  });
  const dictionary = dictionaryQuery.data ?? [];
  const attributeGroups = attributeGroupsQuery.data ?? [];
  const languages = languagesQuery.data ?? [];

  // ── Card-language switcher: per-locale name/description drafts ─────────────────────────────
  const cardLang = useCardLanguageDraft<ProductLocaleFields>(languages, PRODUCT_LOCALE_EMPTY_FIELDS);
  // Destructured once so the effects below depend on the specific stable functions/values they
  // use, not the whole `cardLang` object (a fresh reference every render).
  const { defaultLocale: cardDefaultLocale, seedField: cardSeedField, resetAll: cardResetAll } = cardLang;
  const translationsQuery = useProductTranslations(productId, {
    enabled: canView && isEdit && languages.length > 0,
  });

  // Navigating directly between two products' edit pages (e.g. the parent-product banner link,
  // below) reuses this component instance without unmounting — wipe every locale's draft (locale
  // codes would otherwise collide across different products) and close any open detail drawer.
  //
  // Keep this BEFORE the seed effects. `useProduct` may synchronously return cached product data on
  // the first render; if reset runs after seeding, already-loaded name/description are cleared and
  // the required Name field stays empty until the product query changes again.
  //
  // Skips its own FIRST run (Phase-3 review F5): on a genuine mount there is nothing to reset FROM
  // (the drawer/draft state is already at its freshly-initialized empty value) — and running it
  // anyway raced the `?drawer=prices` seed effect above, since React fires both in the SAME commit
  // on mount and this one, declared later, always ran last, unconditionally clobbering the
  // freshly-seeded drawer back to `null`. The guard only skips the MOUNT invocation; a real
  // productId change later still resets first, exactly as the comment above requires.
  const hasMountedForProduct = useRef(false);
  useEffect(() => {
    if (!hasMountedForProduct.current) {
      hasMountedForProduct.current = true;
      return;
    }
    cardResetAll();
    setOpenDrawer(null);
  }, [productId, cardResetAll]);

  // The DEFAULT language's fields prefill from the resolved product (the reference label);
  // every OTHER language prefills from the dedicated translations endpoint. `seedField` is a
  // per-(locale,field) no-op once seeded, so whichever of these two effects (or the two locale
  // sources for the SAME default locale, in either arrival order) resolves first wins, and neither
  // ever clobbers an in-progress edit.
  useEffect(() => {
    // NOTE: `cardDefaultLocale` is `''` (a valid sentinel, not "not ready") in the empty-registry
    // single-language fallback — guard on `loaded` only, never on `cardDefaultLocale`'s truthiness,
    // or the fallback would never seed the name/description of an existing product.
    if (!loaded) return;
    cardSeedField(cardDefaultLocale, 'name', loaded.name ?? '');
    cardSeedField(cardDefaultLocale, 'description', loaded.description ?? '');
  }, [loaded, cardDefaultLocale, cardSeedField]);

  useEffect(() => {
    const stored = translationsQuery.data;
    if (!stored) return;
    for (const entry of stored) {
      cardSeedField(entry.locale, 'name', entry.name ?? '');
      cardSeedField(entry.locale, 'description', entry.description ?? '');
    }
  }, [translationsQuery.data, cardSeedField]);

  // A variant's parent — fetched only when this product IS a variant, to name it in the banner below.
  const parentProductQuery = useProduct(loaded?.parentProductId ?? undefined, {
    enabled: canView && Boolean(loaded?.parentProductId),
  });
  const hasVariantAxis = resolvedFamily.assignedFamily?.attributes.some((a) => a.variantAxis) ?? false;
  const showVariantsSection = isEdit && Boolean(productId) && hasVariantAxis && !loaded?.parentProductId;

  // ── Hidden-block summaries (adr-merchant-portal-008) ───────────────────────
  // The same query keys the drawer panels use — TanStack Query dedupes, so the summary tiles and
  // the drawers share one fetch each.
  const stockQuery = useProductStock(productId, { enabled: canView && isEdit });
  const barcodesQuery = useProductBarcodes(productId, { enabled: canView && isEdit });
  const priceLinesQuery = useProductPriceLines(productId, {
    enabled: canPricingView && isEdit,
  });
  const variantsQuery = useProductVariants(productId, undefined, {
    enabled: canView && showVariantsSection,
  });

  // ── Derived state ──────────────────────────────────────────────────────────

  // The reference label (adr-catalog-006): the DEFAULT language's draft — regardless of which
  // locale the switcher currently displays. Saving always sends THIS as the flat `name`/
  // `description`, and validity is gated on THIS being filled in, not whichever locale is open.
  const referenceFields = cardLang.fieldsFor(cardLang.defaultLocale);
  const editingDefaultLocale = cardLang.selectedLocale === cardLang.defaultLocale;
  // Hero/preview follows the SELECTED language, falling back to the default language's name (with
  // a hint) when the selected locale has no translation yet.
  const heroNameFallback = !editingDefaultLocale && cardLang.fields.name.trim() === '';
  const heroName = cardLang.fields.name.trim() || referenceFields.name.trim();

  const valid = referenceFields.name.trim() !== '' && draft.categoryId !== '';
  const disabled = !canManage;

  const categoryOptions = useMemo(() => categoriesQuery.data?.content ?? [], [categoriesQuery.data]);

  // Primary image (hero thumbnail) — first uploaded image or the loaded cover.
  const primaryImage = draft.images[0] ?? undefined;

  // Publication state (for readiness panel + hero image).
  const pubData = publicationQuery.data;
  const pubChannels = pubData?.channels ?? [];
  const publishedChannelCodes = pubChannels
    .filter((c) => c.publicationStatus === 'PUBLISHED')
    .map((c) => c.channelCode);
  const isPublishedAnywhere = publishedChannelCodes.length > 0;

  /**
   * Live-computed readiness: card state and commercial availability come from the card,
   * while price readiness comes from web-price-engine-service price lines.
   */
  const now = useMemo(() => new Date(), [priceLinesQuery.data]);
  const currentBaseLine = useMemo(
    () => governingBaseLine(priceLinesQuery.data ?? [], now),
    [priceLinesQuery.data, now],
  );
  const currentBasePrice = unitAmountOf(currentBaseLine);
  // Tile badge/captions + the readiness nuance (design spec §8.1/§8.8, plan-merchant-portal-018
  // WP1.3) — the base slot's scheduled line and its newest non-ACTIVE submission (pending OR
  // negative, told apart by `state`), plus how many OTHER slots (channel/list/group) carry at
  // least one line. Every one of these reads `priceSlots.ts`'s existing base-only helpers — never
  // a re-derivation (adr-merchant-portal-018 Decision 4).
  const scheduledBase = useMemo(
    () => scheduledBaseLine(priceLinesQuery.data ?? [], now),
    [priceLinesQuery.data, now],
  );
  const pendingOrNegativeBase = useMemo(
    () => pendingBaseSubmission(priceLinesQuery.data ?? [], currentBaseLine),
    [priceLinesQuery.data, currentBaseLine],
  );
  const otherPriceSlots = useMemo(
    () => groupBySlot((priceLinesQuery.data ?? []).filter((line) => !isBaseSlot(line))).size,
    [priceLinesQuery.data],
  );

  const readiness = useMemo(
    () =>
      computeReadiness({
        // cardStatus comes from the API (not a form field) — fall back to ACTIVE (backend default).
        cardStatus: pubData?.cardStatus ?? loaded?.cardStatus,
        // commercialAvailability is a form field — use the live draft value.
        commercialAvailability: draft.commercialAvailability,
        published: isPublishedAnywhere,
        publishedChannels: publishedChannelCodes,
        price: currentBasePrice,
        // The three fields below only matter while there is NO governing base line — computeReadiness
        // checks price first, so a governing price short-circuits all of them (§8.8 row 1).
        pricePending:
          !currentBaseLine && pendingOrNegativeBase
            ? pendingOrNegativeBase.state === 'REQUIRES_APPROVAL'
              ? 'REQUIRES_APPROVAL'
              : pendingOrNegativeBase.state === 'BLOCKED' || pendingOrNegativeBase.state === 'REJECTED'
                ? 'BLOCKED'
                : 'PENDING'
            : undefined,
        priceScheduled:
          !currentBaseLine && scheduledBase
            ? { at: scheduledBase.validFrom ?? '', amount: unitAmountOf(scheduledBase) ?? 0 }
            : undefined,
        // §4: a `catalog.view`-only user must not see a FALSE "price missing" — the price-lines
        // query never even ran for them (`enabled: canPricingView && isEdit`, below).
        priceAccessDenied: isEdit && !canPricingView,
      }),
    [
      pubData?.cardStatus,
      loaded?.cardStatus,
      draft.commercialAvailability,
      isPublishedAnywhere,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      publishedChannelCodes.join(','),
      currentBasePrice,
      currentBaseLine,
      pendingOrNegativeBase,
      scheduledBase,
      isEdit,
      canPricingView,
    ],
  );

  // ── Summary-tile data (adr-merchant-portal-008) ────────────────────────────

  const stockItems = stockQuery.data?.items ?? [];
  const totalAvailableQty = stockItems.reduce((sum, row) => sum + row.availableQty, 0);
  const stockUoms = Array.from(new Set(stockItems.map((row) => row.uom)));

  const activeBarcodes = (barcodesQuery.data ?? []).filter((b) => b.status !== 'RETIRED');
  const primaryBarcode = activeBarcodes.find((b) => b.primary) ?? activeBarcodes[0];

  // Content-default locale (adr-tenant-005) — preferred over the portal UI language when resolving
  // translated attribute labels for the variants tile caption.
  const contentDefaultLocale = languages.find((l) => l.defaultLocale)?.locale;
  const axisAttributeLabels = (resolvedFamily.assignedFamily?.attributes ?? [])
    .filter((a) => a.variantAxis)
    .map((a) => {
      const def = dictionary.find((d) => d.code === a.attributeCode);
      return def
        ? resolveLocalizedText(def.translations, 'label', i18n.language, def.code, contentDefaultLocale)
        : a.attributeCode;
    });

  // ── Price tile value/warning/badge/captions (design spec §8.1/§9, plan-merchant-portal-018
  // WP1.3) — loading/error/pre-save/no-access take priority over "no governing price" so the tile
  // never claims "No price" when it simply hasn't been allowed to look, or hasn't finished asking.
  // Pre-save is checked before no-access (mirrors the existing `disabledHint` precedence below): a
  // brand-new product shows "Available after saving" even for a viewer without pricing access.
  const priceLinesSettled = !priceLinesQuery.isLoading && !priceLinesQuery.isError;
  const priceTileHasAccess = isEdit && canPricingView;
  const priceTileValue = !isEdit
    ? t('productEdit.summary.price.noPrice')
    : !canPricingView
      ? '—'
      : priceLinesQuery.isLoading
        ? '…'
        : priceLinesQuery.isError
          ? '—'
          : currentBasePrice != null
            ? formatPrice(currentBasePrice)
            : t('productEdit.summary.price.noPrice');
  const priceTileWarning = priceTileHasAccess && priceLinesSettled && currentBasePrice == null;
  const priceTileBadgeValue =
    priceTileHasAccess && priceLinesSettled ? priceTileBadge(pendingOrNegativeBase, t) : undefined;
  const priceTileCaptionLines =
    priceTileHasAccess && !priceLinesQuery.isLoading
      ? priceLinesQuery.isError
        ? [t('productEdit.summary.price.loadError')]
        : priceTileCaptions(t, {
            governing: currentBaseLine,
            scheduled: scheduledBase,
            pendingOrNegative: pendingOrNegativeBase,
            otherSlots: otherPriceSlots,
          })
      : [];

  const summaryTiles: ProductSummaryTile[] = [
    {
      key: 'prices',
      icon: <SellRoundedIcon />,
      label: t('productEdit.summary.price.label'),
      value: priceTileValue,
      captions: priceTileCaptionLines,
      badge: priceTileBadgeValue,
      warning: priceTileWarning,
      onOpen: () => setOpenDrawer('prices'),
      // R5 fix (plan-merchant-portal-018 WP1.2, adr-merchant-portal-018): `catalog.pricing.view`
      // now OPENS the drawer (read-only there — see ProductPricesDrawer's own canManage gate);
      // only the complete absence of pricing access disables the tile.
      disabled: !isEdit || !canPricingView,
      disabledHint: !isEdit
        ? t('productEdit.summary.availableAfterSave')
        : t('productEdit.summary.price.noAccess'),
      // The badge's own label is folded into the accessible name too (design spec §13) — e.g.
      // "Open prices — Awaiting approval" — since the badge chip itself is non-interactive.
      openAriaLabel: priceTileBadgeValue
        ? `${t('productEdit.summary.price.openAria')} — ${priceTileBadgeValue.label}`
        : t('productEdit.summary.price.openAria'),
    },
    {
      key: 'stock',
      icon: <WarehouseRoundedIcon />,
      label: t('productEdit.summary.stock.label'),
      // plan-catalog-007: the card carries no availability flag — the tile is the LIVE ledgered
      // inventory summary only (untracked products show a dash + hint).
      value:
        isEdit && stockItems.length > 0
          ? `${formatQuantity(totalAvailableQty)}${stockUoms.length === 1 ? ` ${stockUoms[0]}` : ''}`
          : '—',
      caption:
        isEdit && stockItems.length > 0
          ? t('productEdit.summary.stock.locations', { n: stockItems.length })
          : t('productEdit.summary.stock.untracked'),
      warning: false,
      onOpen: () => setOpenDrawer('stock'),
      openAriaLabel: t('productEdit.summary.stock.openAria'),
    },
    {
      key: 'barcodes',
      icon: <QrCode2RoundedIcon />,
      label: t('productEdit.summary.barcodes.label'),
      value: primaryBarcode?.value ?? t('productEdit.summary.barcodes.none'),
      mono: Boolean(primaryBarcode),
      caption:
        activeBarcodes.length > 1
          ? t('productEdit.summary.barcodes.more', { n: activeBarcodes.length - 1 })
          : primaryBarcode
            ? t('barcodes.primaryBadge')
            : undefined,
      disabled: !isEdit,
      disabledHint: t('productEdit.summary.availableAfterSave'),
      onOpen: () => setOpenDrawer('barcodes'),
      openAriaLabel: t('productEdit.summary.barcodes.openAria'),
    },
    ...(showVariantsSection
      ? [
          {
            key: 'variants',
            icon: <CallSplitRoundedIcon />,
            label: t('productEdit.summary.variants.label'),
            value: variantsQuery.data ? formatNumber(variantsQuery.data.length) : '…',
            caption:
              axisAttributeLabels.length > 0
                ? t('productEdit.summary.variants.axisCaption', { axis: axisAttributeLabels.join(', ') })
                : undefined,
            onOpen: () => setOpenDrawer('variants'),
            openAriaLabel: t('productEdit.summary.variants.openAria'),
          } satisfies ProductSummaryTile,
        ]
      : []),
  ];

  /** Field label + the selected content language's name (e.g. "Name — Deutsch"). */
  const withLanguageSuffix = (label: string): string =>
    cardLang.enabledLanguages.length > 0
      ? `${label} — ${
          cardLang.enabledLanguages.find((l) => l.locale === cardLang.selectedLocale)?.name ??
          cardLang.selectedLocale
        }`
      : label;

  // ── Save ───────────────────────────────────────────────────────────────────

  /**
   * `translations[]` for the save payload — ONLY the locales the merchant actually touched this
   * session (per-locale dirty tracking; unedited locales are left untouched server-side by
   * omission, per the upsert's merge semantics). A touched-but-blanked-out name is dropped rather
   * than sent (the schema requires a non-empty `name` per entry) — clearing a translation entirely
   * isn't this form's job. `undefined` (not `[]`) when the tenant has no configured languages or
   * nothing was touched, so an already-omitted field stays omitted rather than becoming an explicit
   * (and pointless) empty array.
   */
  const buildTranslationsPayload = (): ProductTranslationInput[] | undefined => {
    if (cardLang.enabledLanguages.length === 0) return undefined;
    const entries = cardLang
      .touchedEntries()
      .filter((entry) => entry.name.trim() !== '')
      .map((entry) => ({
        locale: entry.locale,
        name: entry.name.trim(),
        description: entry.description.trim() || undefined,
      }));
    return entries.length > 0 ? entries : undefined;
  };

  const handleSave = () => {
    if (!valid) return;
    const body: ProductUpsertRequest = {
      // The reference label (adr-catalog-006): always the DEFAULT language's draft, regardless of
      // which locale the switcher currently shows — see `referenceFields`'s doc, above.
      name: referenceFields.name.trim(),
      code: draft.code.trim() || undefined,
      categoryId: draft.categoryId,
      categoryName: draft.categoryName,
      brand: draft.brand.trim() || undefined,
      unit: draft.unit.trim() || undefined,
      unitOkeiCode: draft.unitOkeiCode || undefined,
      description: referenceFields.description.trim() || undefined,
      imageUrl: draft.images[0],
      images: draft.images.length ? draft.images : undefined,
      // Absent ≠ empty on the master upsert: an omitted attributes[] leaves the product's imported
      // (pass-through) characteristics untouched, an explicit [] would CLEAR them. The master card no
      // longer serves the legacy pairs (they surface as typed attributeValues instead), so this form
      // can no longer echo them back — omit the field unless the user actually entered legacy rows.
      attributes: (() => {
        const rows = draft.attributes.filter((a) => a.name.trim() && a.value.trim());
        return rows.length ? rows : undefined;
      })(),
      translations: buildTranslationsPayload(),
    };
    // `id` is the resolved product UUID — either the existing one (edit) or a freshly
    // generated UUID (create). Using `id` (not `productId`) in both mutations ensures
    // commercialAvailability is persisted on first save too, not just on subsequent edits.
    const id = productId ?? newProductId();
    // Bug fix (2026-08-25): only follow up with the state-update call when it can actually change
    // something — either this is the product's FIRST save (nothing persisted yet for the master's
    // upsert-default to possibly have landed on the wrong value) or the merchant actually edited
    // availability. An unconditional re-send here used to make web-catalog-master-service publish a
    // SECOND, real `catalog.product-master` Kafka event on every single save — even one that only
    // touched e.g. the description — because it re-submitted the SAME, already-persisted value every
    // time. See the paired fix in that service's `UpsertOutcome`/`CatalogIngestionServiceImpl` for
    // the other half (defense in depth: that fix alone would already have closed this, but skipping
    // the redundant call here is also just less network chatter for no purpose).
    // `loaded` (productQuery.data) is typed `ProductDetail | undefined` — TS can't see that a
    // clickable Save button in edit mode implies the query already resolved (the narrowing from
    // the `if (!loaded) return` guards elsewhere lives in OTHER closures/effects, not this one) —
    // so this reads it optionally, with the same 'AVAILABLE' fallback used when seeding the draft.
    const commercialAvailabilityChanged =
      !isEdit || draft.commercialAvailability !== (loaded?.commercialAvailability ?? 'AVAILABLE');
    upsert.mutate(
      { productId: id, body },
      {
        onSuccess: async () => {
          try {
            if (commercialAvailabilityChanged) {
              await updateState.mutateAsync({
                productId: id,
                body: { commercialAvailability: draft.commercialAvailability },
              });
            }
            success(isEdit ? t('productEdit.saved') : t('productEdit.created'));
            navigate('/catalog/products');
          } catch (mutationError) {
            notifyError(
              mutationError instanceof Error
                ? mutationError.message
                : t('productEdit.stateUpdateError'),
            );
          }
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  // ── Guard states ───────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <PageContainer title={t('productEdit.pageTitleFallback')}>
        <ErrorState
          title={t('productEdit.accessRestrictedTitle')}
          message={t('productEdit.accessRestrictedMessage')}
        />
      </PageContainer>
    );
  }
  if (isEdit && productQuery.isLoading) {
    return (
      <PageContainer title={t('productEdit.pageTitleFallback')}>
        <LoadingState label={t('productEdit.loading')} />
      </PageContainer>
    );
  }
  if (isEdit && productQuery.isError) {
    return (
      <PageContainer title={t('productEdit.pageTitleFallback')}>
        <ErrorState
          title={t('productEdit.loadError.title')}
          message={productQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void productQuery.refetch()}
        />
      </PageContainer>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageContainer
      title={isEdit ? heroName || t('productEdit.pageTitleFallback') : t('productEdit.newProductTitle')}
      subtitle={isEdit ? t('productEdit.editSubtitle') : t('productEdit.createSubtitle')}
      maxWidth="100%"
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/catalog/products')}
          >
            {t('common:action.back')}
          </Button>
          {isEdit && productId ? (
            <Button startIcon={<HistoryRoundedIcon />} onClick={() => setHistoryOpen(true)}>
              {t('productEdit.historyButton')}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              variant="contained"
              startIcon={
                upsert.isPending || updateState.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              disabled={!valid || upsert.isPending || updateState.isPending}
              onClick={handleSave}
            >
              {t('common:action.save')}
            </Button>
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={2.5}>

        {/* Variant-of-parent banner (user-story-merchant-portal-031) — this product was created
            via "Create variants" on another product's variant axis. */}
        {isEdit && loaded?.parentProductId ? (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                onClick={() => navigate(`/catalog/products/${loaded.parentProductId}`)}
              >
                {t('productEdit.parentBanner.action')}
              </Button>
            }
          >
            {t('productEdit.parentBanner.message', {
              name: parentProductQuery.data?.name ?? t('productEdit.parentBanner.fallbackName'),
            })}
            {/* epic-catalog-007 P8: inheritance provenance + one-click reset to the model's value. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                color={loaded.categoryInherited ? 'default' : 'secondary'}
                label={`${t('inheritance.categoryLabel')}: ${
                  loaded.categoryInherited ? t('inheritance.inheritedFromModel') : t('inheritance.overridden')
                }`}
                onDelete={
                  canManage && loaded.categoryInherited === false
                    ? () =>
                        resetCategoryOverride.mutate(
                          { productId: loaded.id },
                          { onSuccess: () => notify('success', t('inheritance.resetDone')) },
                        )
                    : undefined
                }
                deleteIcon={<ReplayRoundedIcon titleAccess={t('inheritance.resetToModel')} />}
              />
              <Chip
                size="small"
                variant="outlined"
                color={loaded.imagesInherited ? 'default' : 'secondary'}
                label={`${t('inheritance.photosLabel')}: ${
                  loaded.imagesInherited ? t('inheritance.inheritedFromModel') : t('inheritance.overridden')
                }`}
                onDelete={
                  canManage && loaded.imagesInherited === false
                    ? () =>
                        resetImagesOverride.mutate(
                          { productId: loaded.id },
                          { onSuccess: () => notify('success', t('inheritance.resetDone')) },
                        )
                    : undefined
                }
                deleteIcon={<ReplayRoundedIcon titleAccess={t('inheritance.resetToModel')} />}
              />
              {loaded.descriptionInherited != null ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color={loaded.descriptionInherited ? 'default' : 'secondary'}
                  label={`${t('inheritance.descriptionLabel')}: ${
                    loaded.descriptionInherited
                      ? t('inheritance.inheritedFromModel')
                      : t('inheritance.overridden')
                  }`}
                />
              ) : null}
            </Stack>
          </Alert>
        ) : null}

        {/* View-only notice */}
        {disabled ? (
          <Alert severity="info">{t('productEdit.viewOnlyNotice')}</Alert>
        ) : null}

        {/* ═══════════════════════════════════════════════════════════════════
            COMPACT CARD LAYOUT (adr-merchant-portal-008)
            Left (8): hero (photo · language · identity · summary tiles) →
                      basic info → characteristics
            Right (4): availability + buyer state + readiness → publication
            Hidden blocks (prices / stock / barcodes / variants) live in
            LEFT-anchored drawers, launched from the hero's summary tiles.
            ═══════════════════════════════════════════════════════════════ */}
        <Grid container spacing={2.5} alignItems="flex-start">

          {/* ── Left column ───────────────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={2.5}>

              {/* Hero card — identity + the single card-language switcher + summary tiles */}
              <Card variant="outlined">
                <CardContent
                  sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    {/* Photo + gallery management */}
                    <Box sx={{ width: { sm: 220 }, flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {primaryImage ? (
                          <Box
                            component="img"
                            src={primaryImage}
                            alt={heroName || t('productEdit.pageTitleFallback')}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                          />
                        ) : (
                          <Inventory2RoundedIcon sx={{ fontSize: 56, color: 'action.disabled' }} />
                        )}
                      </Box>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, letterSpacing: '0.08em', display: 'block', mt: 1.5, mb: 1 }}
                      >
                        {t('productEdit.gallerySection')}
                      </Typography>
                      <MediaManager
                        ownerId={productId}
                        images={draft.images}
                        onChange={(images) => setField('images', images)}
                        disabled={disabled}
                        onResult={(severity, message) =>
                          severity === 'success' ? success(message) : notifyError(message)
                        }
                      />
                    </Box>

                    {/* Identity + the single content-language switcher + summary tiles */}
                    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                      {/* ONE switcher for ALL card content (name, description, translatable
                          attributes) — exactly one language is displayed at a time. Switching is a
                          read operation, so it stays enabled for view-only users too. */}
                      {cardLang.enabledLanguages.length > 0 ? (
                        <Stack spacing={0.75}>
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
                          >
                            {t('productEdit.cardLanguage.switcherLabel')}
                          </Typography>
                          <CardLanguageSwitcher
                            languages={cardLang.enabledLanguages}
                            selectedLocale={cardLang.selectedLocale}
                            onSelect={cardLang.selectLocale}
                            isFilled={cardLang.isFilled}
                          />
                          {cardLang.isDirty ? (
                            <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                              {t('productEdit.cardLanguage.unsavedHint')}
                            </Alert>
                          ) : null}
                        </Stack>
                      ) : null}

                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{
                            lineHeight: 1.2,
                            mb: 0.5,
                            color: heroName ? 'text.primary' : 'text.disabled',
                          }}
                        >
                          {heroName || t('productEdit.productNamePlaceholder')}
                        </Typography>
                        {heroNameFallback ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {t('productEdit.cardLanguage.noTranslationHint')}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          {[
                            draft.code && t('productEdit.skuPrefix', { code: draft.code }),
                            draft.brand,
                            draft.categoryName,
                          ]
                            .filter(Boolean)
                            .join(' · ') || (isEdit ? '' : t('productEdit.fillBasicInfoHint'))}
                        </Typography>
                      </Box>

                      <Divider />

                      {/* Summary tiles — the "current value" of every hidden detail block */}
                      <Box>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
                        >
                          {t('productEdit.summary.title')}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <ProductSummaryTiles tiles={summaryTiles} />
                        </Box>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Basic product information — name/description follow the selected card language */}
              <Section title={t('productEdit.sections.basicInfo')}>
                <Stack spacing={2.5}>
                  <TextField
                    label={withLanguageSuffix(t('productEdit.nameLabel'))}
                    required={editingDefaultLocale}
                    value={cardLang.fields.name}
                    onChange={(e) => cardLang.setField('name', e.target.value)}
                    error={!disabled && editingDefaultLocale && cardLang.fields.name.trim() === ''}
                    disabled={disabled}
                    fullWidth
                  />
                  <TextField
                    label={withLanguageSuffix(t('productEdit.descriptionLabel'))}
                    value={cardLang.fields.description}
                    onChange={(e) => cardLang.setField('description', e.target.value)}
                    disabled={disabled}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <TextField
                      label={t('productEdit.skuLabel')}
                      value={draft.code}
                      onChange={(e) => setField('code', e.target.value)}
                      disabled={disabled}
                      fullWidth
                    />
                    <TextField
                      label={t('productEdit.brandLabel')}
                      value={draft.brand}
                      onChange={(e) => setField('brand', e.target.value)}
                      disabled={disabled}
                      fullWidth
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <TextField
                      select
                      label={t('productEdit.categoryLabel')}
                      required
                      value={draft.categoryId}
                      onChange={(e) => {
                        const cat = categoryOptions.find((c) => c.id === e.target.value);
                        setDraft((d) => ({
                          ...d,
                          categoryId: e.target.value,
                          categoryName: cat?.name ?? d.categoryName,
                        }));
                      }}
                      error={!disabled && draft.categoryId === ''}
                      disabled={disabled}
                      fullWidth
                      helperText={
                        categoryOptions.length === 0 ? t('productEdit.noCategories') : undefined
                      }
                    >
                      {draft.categoryId && !categoryOptions.some((c) => c.id === draft.categoryId) ? (
                        <MenuItem value={draft.categoryId}>
                          {draft.categoryName || draft.categoryId}
                        </MenuItem>
                      ) : null}
                      {categoryOptions.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ProductUnitField
                      unit={draft.unit}
                      unitOkeiCode={draft.unitOkeiCode}
                      onChange={({ unit, unitOkeiCode }) =>
                        setDraft((d) => ({ ...d, unit, unitOkeiCode }))
                      }
                      disabled={disabled}
                    />
                  </Stack>
                </Stack>
              </Section>

              {/* Characteristics — the assigned attribute template (auto-resolved when the card
                  opens) + the typed attribute rows. Edit mode only: family assignment and
                  attribute values operate on an already-saved productId. */}
              {isEdit && productId ? (
                <Section title={t('productEdit.sections.attributes')}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
                      >
                        {t('productEdit.sections.family')}
                      </Typography>
                      <ProductFamilyPanel
                        productId={productId}
                        families={resolvedFamily.families}
                        familiesLoading={resolvedFamily.familiesLoading}
                        assignedFamily={resolvedFamily.assignedFamily}
                        unresolvable={resolvedFamily.unresolvable}
                        attributeValues={loaded?.attributeValues ?? []}
                        attributeCompleteness={loaded?.attributeCompleteness}
                        dictionary={dictionary}
                        canManage={canManage}
                        onNotify={notify}
                        onFamilyCodeAssigned={resolvedFamily.markAssignedCode}
                      />
                    </Box>
                    <Divider />
                    <ProductAttributesPanel
                      productId={productId}
                      assignedFamily={resolvedFamily.assignedFamily}
                      attributeValues={loaded?.attributeValues ?? []}
                      legacyAttributes={loaded?.attributes ?? []}
                      dictionary={dictionary}
                      attributeGroups={attributeGroups}
                      languages={languages}
                      selectedLocale={cardLang.selectedLocale}
                      canManage={canManage}
                      onNotify={notify}
                    />
                  </Stack>
                </Section>
              ) : null}
            </Stack>
          </Grid>

          {/* ── Right column: availability + buyer state + readiness → publication ── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2.5}>
              <Card variant="outlined">
                <CardContent
                  sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}
                >
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t('productEdit.commercialAvailability.title')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                      {t('productEdit.commercialAvailability.hint')}
                    </Alert>
                    <TextField
                      select
                      label={t('productEdit.commercialAvailability.title')}
                      value={draft.commercialAvailability}
                      onChange={(e) =>
                        setField('commercialAvailability', e.target.value as CommercialAvailability)
                      }
                      disabled={disabled || updateState.isPending}
                      fullWidth
                    >
                      {COMMERCIAL_AVAILABILITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Backend-computed buyer state (edit only, after load) */}
                    {isEdit && loaded?.customerState ? (
                      <CustomerStatePreview
                        state={loaded.customerState.state}
                        orderable={loaded.customerState.orderable}
                        showPrice={loaded.customerState.showPrice}
                        reasonCode={loaded.customerState.reasonCode}
                      />
                    ) : null}

                    <Divider />

                    {/* Readiness checklist — LIVE-UPDATING as form fields change */}
                    <Box>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
                      >
                        {t('productEdit.readinessLabel')}
                      </Typography>
                      <Box sx={{ mt: 1.5 }}>
                        {isEdit && publicationQuery.isLoading ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                              {t('productEdit.checkingStatuses')}
                            </Typography>
                          </Stack>
                        ) : (
                          <ReadinessChecklist readiness={readiness} />
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Publication card — channel diagnostics + master lifecycle actions */}
              {isEdit && productId ? (
                <ProductPublicationCard
                  productId={productId}
                  onResult={(severity, message) =>
                    severity === 'success' ? success(message) : notifyError(message)
                  }
                />
              ) : null}
            </Stack>
          </Grid>

        </Grid>
      </Stack>

      {/* ═══════════════════════════════════════════════════════════════════
          DETAIL DRAWERS (adr-merchant-portal-008)
          Prices mutate immediately through price-engine; Stock is a live, read-only inventory
          summary (edits: grid quick-set / the /inventory page); Barcodes & Variants mutate
          immediately through their own endpoints.
          ═══════════════════════════════════════════════════════════════ */}

      {/* Prices — the card's Prices-360 (plan-merchant-portal-018 WP1.2, adr-merchant-portal-018) */}
      <ProductPricesDrawer
        open={openDrawer === 'prices'}
        product={productId ? { id: productId, name: heroName || loaded?.name } : { id: '' }}
        canManage={canPricingManage}
        onClose={() => setOpenDrawer(null)}
        onResult={notify}
      />

      {/* Stock */}
      <ProductSectionDrawer
        open={openDrawer === 'stock'}
        onClose={() => setOpenDrawer(null)}
        title={t('productEdit.drawers.stock.title')}
        subtitle={t('productEdit.drawers.stock.subtitle')}
        icon={<WarehouseRoundedIcon fontSize="small" />}
      >
        <Stack spacing={2.5}>
          {/* plan-catalog-007: the manual availability flag is gone — availability derives from
              the ledgered inventory quantities below; edits go through the grid quick-set dialog
              or the dedicated /inventory page. */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ lineHeight: 1.2, letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
            >
              {t('productEdit.sections.stockByLocation')}
            </Typography>
            {/* Product-360: this product's LIVE, per-location stock snapshot from
                web-inventory-service — read-only summary; full management stays the dedicated
                /inventory page. Existing products only — a not-yet-saved product has no rows. */}
            {isEdit && productId ? (
              <ProductStockPanel productId={productId} />
            ) : (
              <Alert severity="info">{t('productEdit.drawers.liveStockAfterSave')}</Alert>
            )}
          </Box>
        </Stack>
      </ProductSectionDrawer>

      {/* Barcodes (user-story-merchant-portal-039) — tile is disabled pre-save, so this only ever
          opens for an existing product. */}
      <ProductSectionDrawer
        open={openDrawer === 'barcodes'}
        onClose={() => setOpenDrawer(null)}
        title={t('productEdit.sections.barcodes')}
        icon={<QrCode2RoundedIcon fontSize="small" />}
      >
        {isEdit && productId ? (
          <ProductBarcodesPanel productId={productId} canManage={canManage} onNotify={notify} />
        ) : null}
      </ProductSectionDrawer>

      {/* Variants (user-story-merchant-portal-031) — only when the assigned family has a variant
          axis and this product isn't itself a variant. */}
      {showVariantsSection && resolvedFamily.assignedFamily ? (
        <ProductSectionDrawer
          open={openDrawer === 'variants'}
          onClose={() => setOpenDrawer(null)}
          title={t('productEdit.sections.variants')}
          icon={<CallSplitRoundedIcon fontSize="small" />}
        >
          <ProductVariantsSection
            productId={productId as string}
            productCode={draft.code}
            assignedFamily={resolvedFamily.assignedFamily}
            dictionary={dictionary}
            canManage={canManage}
            onNotify={notify}
          />
        </ProductSectionDrawer>
      ) : null}

      {/* ── Version history (user-story-merchant-portal-032) ─────────────────── */}
      {isEdit && productId ? (
        <VersionHistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          subjectLabel={referenceFields.name || t('productEdit.pageTitleFallback')}
          entityId={productId}
          usePage={useProductVersions}
          useDetail={useProductVersion}
          restore={{
            canRestore: canManage,
            isPending: restoreVersion.isPending,
            onRestore: (number) =>
              restoreVersion.mutate(
                { productId, number },
                {
                  onSuccess: () => {
                    success(t('versionHistoryDrawer.restoreSuccess', { number }));
                    // Close rather than stay on the now-superseded diff — reopening "History"
                    // shows the fresh restore entry at the top of the list.
                    setHistoryOpen(false);
                  },
                  onError: (error) => notifyError(error.message),
                },
              ),
          }}
        />
      ) : null}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
